import { css } from 'styled-components'

import { ButtonProps } from '@customTypes'
import { palette, transitions } from '_styles/variables'

import { ButtonStyles } from '../helpers/types'
import { getEdgeRadius } from './edge-radius'

const BaseStyle = css<ButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  line-height: 1.5rem;
  border-radius: ${getEdgeRadius};
  font-size: 1rem;

  padding: ${({ slim }) =>
    slim ? '0.375rem 1.6875rem 0.3125rem 1.75rem' : '0.875rem 1.25rem 0.75rem'};

  font-weight: bold;
  box-sizing: border-box;
  cursor: pointer;
  height: max-content;

  &[aria-disabled='true'],
  &[aria-busy='true'] {
    cursor: not-allowed !important;
  }
`

const PrimaryButton = css<ButtonProps>`
  ${BaseStyle};
  border: none;
  background-color: ${palette.brand.pink};
  color: ${palette.neutral.white};
  transition: ${transitions.quick(['box-shadow'])};

  &:focus:not([aria-disabled='true']):not([aria-busy='true']) {
    box-shadow: ${palette.brand.pink} inset 0px 0px 0px 0.125rem,
      ${palette.neutral.white} inset 0px 0px 0px 0.25rem;
    outline: none;
  }

  &:hover:not([aria-disabled='true']):not([aria-busy='true']),
  &:active:not([aria-disabled='true']):not([aria-busy='true']) {
    box-shadow: 0 0.25rem 0.75rem ${palette.pink[500]}50;
    outline: none;

    &:focus {
      box-shadow: 0 0.25rem 0.75rem ${palette.pink[500]}50,
        ${palette.brand.pink} inset 0px 0px 0px 0.125rem,
        ${palette.neutral.white} inset 0px 0px 0px 0.25rem;
    }
  }

  &[aria-disabled='true'] {
    background-color: ${palette.brand.pink}30;
  }
`

const SecondaryButton = css<ButtonProps>`
  ${BaseStyle};
  border: 1px solid ${palette.brand.pink};
  background-color: transparent;
  color: ${palette.brand.pink};

  &:focus:not([aria-disabled='true']):not([aria-busy='true']) {
    background-color: ${palette.pink[100]};
    outline: none;
  }

  &:hover:not([aria-disabled='true']):not([aria-busy='true']),
  &:active:not([aria-disabled='true']):not([aria-busy='true']) {
    box-shadow: 0 0.25rem 0.75rem ${palette.pink[500]}50;
  }

  &[aria-disabled='true'] {
    color: ${palette.brand.pink}30;
    border-color: ${palette.brand.pink}30;
  }
`

const ShowMoreButton = css<ButtonProps>`
  ${BaseStyle};
  border: 1px solid ${palette.ui.blue};
  background-color: transparent;
  color: ${palette.ui.blue};

  &:focus {
    background-color: ${palette.ui.blue}15;
    outline: none;
  }
`

const LinkButton = css`
  border: none;
  background-color: transparent;
  color: ${palette.brand.pink};
  padding: 0.375rem 1.6875rem 0.3125rem 1.75rem;
  font-size: 1rem;
  width: max-content;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-weight: inherit;

  &:hover,
  &:visited {
    outline: none;
    border-bottom: 0.0625rem solid currentColor;
    margin-bottom: -0.0625rem;
  }

  &:active,
  &:focus {
    outline: none;
    border-bottom: 0.25rem solid currentColor;
    margin-bottom: -0.25rem;
  }
`

const buttonStyles: ButtonStyles = {
  primary: PrimaryButton,
  secondary: SecondaryButton,
  'show-more': ShowMoreButton,
  link: LinkButton,
}

export const getButtonStyle = ({ buttonType = 'primary' }: Pick<ButtonProps, 'buttonType'>) =>
  buttonStyles[buttonType]
